import { useCallback, useEffect } from 'react';
import { useWatch } from 'react-hook-form';
import {
  PRODUCT_TYPE, SUPPLIER_LINKS, VALUE_CHAIN_LIST, VALUE_CHAIN_SECTION,
} from 'constants/formFields';
import {
  getProductTypeOptionByValueChain,
  getValueChainByProductType,
} from 'utils/formHelpers/productTypeValueChain';

import type IOption from 'components/MappingModal/interfaces/IOption';
import type { IUseProductTypeValueChain } from './interfaces/IUseProductTypeValueChain';

const useProductTypeValueChain = ({
  control,
  handleGetVcOptions,
  setValue,
  vcOptionsList,
}: IUseProductTypeValueChain) => {
  const valueChainList = useWatch({ control, name: VALUE_CHAIN_LIST });
  const productType = useWatch({ control, name: PRODUCT_TYPE });

  const handleChangeValueChain = useCallback((
    selectedValueChain: IOption,
    onChangeValueChain: (value: IOption) => void,
  ) => {
    const productTypeOption = getProductTypeOptionByValueChain(selectedValueChain.value);

    onChangeValueChain(selectedValueChain);
    handleGetVcOptions(selectedValueChain.value);
    setValue(VALUE_CHAIN_SECTION, undefined as never);
    // The new section allows a different unit type, so existing links no longer apply.
    setValue(SUPPLIER_LINKS, []);

    if (!productTypeOption) {
      setValue(PRODUCT_TYPE, undefined as never);
      return;
    }

    setValue(PRODUCT_TYPE, productTypeOption);
  }, [handleGetVcOptions, setValue]);

  const handleChangeProductType = useCallback((
    selectedProductType: IOption,
    onChangeProductType: (value: IOption) => void,
  ) => {
    const valueChainValue = getValueChainByProductType(selectedProductType.value);

    onChangeProductType(selectedProductType);

    if (!valueChainValue || valueChainList?.value === valueChainValue) {
      return;
    }

    const valueChainOption = vcOptionsList?.find(({ value }) => value === valueChainValue) || {
      value: valueChainValue,
      label: valueChainValue,
    };

    setValue(VALUE_CHAIN_LIST, valueChainOption);
    handleGetVcOptions(valueChainValue);
    setValue(VALUE_CHAIN_SECTION, undefined as never);
    setValue(SUPPLIER_LINKS, []);
  }, [handleGetVcOptions, setValue, valueChainList?.value, vcOptionsList]);

  useEffect(() => {
    const productTypeOption = getProductTypeOptionByValueChain(valueChainList?.value);

    if (!productTypeOption) {
      if (productType?.value) {
        setValue(PRODUCT_TYPE, undefined as never);
      }

      return;
    }

    if (productType?.value === productTypeOption.value) {
      return;
    }

    setValue(PRODUCT_TYPE, productTypeOption);
  }, [productType?.value, setValue, valueChainList?.value]);

  return {
    handleChangeProductType,
    handleChangeValueChain,
  };
};

export default useProductTypeValueChain;
